import { useQuery } from '@tanstack/react-query';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { useCallback } from 'react';

import usePublicApiQuery from '@/hooks/api/usePublicApiQuery';
import { SimpleErrorResponse } from '@/models/Error';
import { StepSummary } from '@/pages/BuildDetailsPage/types/BuildLogSummary';
import { buildEnvironmentSetupPath, stepLogPath } from '@/utils/publicApiRouteService';

import StepLogResponse, { StepLogChunk } from '../../../types/StepLogResponse';
import { StepLogQueryKeys } from '../utils/BuildLogQueryKeys';

export type UseStepLogChunksProps = {
  appSlug: string;
  buildSlug: string;
  viewedAsPublicPage: boolean;
  uuid: StepSummary['uuid'] | 'build-environment-setup';
  enabled: boolean;
};

export const useStepLogChunks = ({ appSlug, buildSlug, enabled, uuid, viewedAsPublicPage }: UseStepLogChunksProps) => {
  const {
    data: stepLogResponse,
    error: stepLogError,
    isError: isStepLogError,
    isLoading: isStepLogLoading,
    isSuccess: isStepLogSuccess,
    refetch: refetchStepLog,
  } = usePublicApiQuery<StepLogResponse, SimpleErrorResponse>(
    StepLogQueryKeys.detail(uuid),
    uuid === 'build-environment-setup'
      ? buildEnvironmentSetupPath(appSlug, buildSlug)
      : stepLogPath(appSlug, buildSlug, uuid),
    {
      enabled,
      requestConfig: {
        publicAccess: viewedAsPublicPage,
      },
      retry: false,
      staleTime: Infinity,
    },
  );

  const {
    data: archivedStepLogResponse,
    error: archivedStepLogError,
    isError: isArchivedStepLogError,
    isLoading: isArchivedStepLogLoading,
    isSuccess: isArhivedStepLogSuccess,
    refetch: refetchArchivedStepLog,
  } = useQuery<AxiosResponse<StepLogChunk[]>, AxiosError<Error>, StepLogChunk[]>({
    enabled: Boolean(enabled && stepLogResponse?.expiringRawLogUrl),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: async () => axios.get<StepLogChunk[]>(stepLogResponse!.expiringRawLogUrl!),
    queryKey: StepLogQueryKeys.archivedDetail(uuid),
    retry: false,
    select: (response) => response.data,
    staleTime: Infinity,
  });

  const isDeleted = stepLogError?.response?.status === 404 || archivedStepLogError?.response?.status === 404;
  const chunks = archivedStepLogResponse || stepLogResponse?.logChunks || [];
  const refetch = useCallback(() => {
    if (isStepLogError) {
      refetchStepLog();
    }
    if (isArchivedStepLogError) {
      refetchArchivedStepLog();
    }
  }, [isStepLogError, refetchStepLog, isArchivedStepLogError, refetchArchivedStepLog]);

  return {
    chunks,
    isDeleted,
    isError: isStepLogError || isArchivedStepLogError,
    isLoading: isStepLogLoading || isArchivedStepLogLoading,
    isSuccess: (isStepLogSuccess && !stepLogResponse?.expiringRawLogUrl) || isArhivedStepLogSuccess,
    refetch,
  };
};
